import React, { useEffect, useState, useContext } from 'react';
import { Badge, Box, Flex, Heading, Text, VStack, Button } from '@chakra-ui/react';
import { Download } from 'lucide-react';
import { invoiceTexts } from "../i18n/invoice";
import { LanguageContext } from "../contexts/LanguageContext";
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const STATUS_PALETTE = {
  Paid: "green",
  Sent: "orange",
  Issued: "gray",
};

function Invoices() {
  const { language } = useContext(LanguageContext);
  const t = invoiceTexts[language] || invoiceTexts["et"];

  const [agreements, setAgreements] = useState([]);

  const handleDownload = async (invoiceId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/invoices/download/${invoiceId}/`,
        {
          responseType: 'blob',
          withCredentials: true,
        }
      );

      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from response headers if available
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : `invoice_${invoiceId}.pdf`;

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading invoice:", error);
      alert("Failed to download invoice");
    }
  };

  useEffect(() => {
    async function fetchAgreements() {
      const response = await fetch(`${API_BASE_URL}/api/invoices/`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setAgreements(data);
      } else {
        console.error('Failed to fetch agreements and invoices');
      }
    }

    fetchAgreements();
  }, []);

  return (
    <Box minH="calc(100vh - 4rem)" bg="gray.50" py={{ base: 8, md: 12 }} px={4}>
      <Box maxW="3xl" mx="auto">
        <Box textAlign="center" mb={8}>
          <Text
            fontFamily="space"
            fontSize="xs"
            letterSpacing="0.3em"
            textTransform="uppercase"
            color="gray.500"
            mb={2}
          >
            akordion.ee
          </Text>
          <Heading fontFamily="heading" fontWeight="500" fontSize="3xl" color="gray.900">
            {t.invoices}
          </Heading>
        </Box>

        <VStack gap={6} align="stretch">
          {agreements.map((agreement) => (
            <Box
              key={agreement.agreement.agreementId}
              bg="white"
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="lg"
              boxShadow="sm"
              p={{ base: 5, md: 8 }}
            >
              <Heading fontFamily="heading" fontWeight="600" fontSize="2xl" color="gray.900">
                {t.agreement} #{agreement.agreement.agreementId}
              </Heading>
              <Text
                fontFamily="space"
                fontSize="xs"
                letterSpacing="0.15em"
                textTransform="uppercase"
                color="gray.500"
                mt={1}
                mb={4}
              >
                {t.ref} {agreement.agreement.referenceNr}
              </Text>

              {agreement.invoices.length > 0 ? (
                <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" overflow="hidden">
                  {agreement.invoices.map((invoice) => (
                    <Flex
                      key={invoice.id}
                      align="center"
                      justify="space-between"
                      gap={3}
                      wrap="wrap"
                      px={4}
                      py={3}
                      borderBottomWidth="1px"
                      borderColor="gray.100"
                      _last={{ borderBottomWidth: 0 }}
                      _hover={{ bg: "gray.50" }}
                    >
                      <Box>
                        <Text fontSize="sm" fontWeight="medium" color="gray.900">
                          {t.invoice} {invoice.id} · {invoice.date}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          {invoice.price}€
                        </Text>
                      </Box>
                      <Flex align="center" gap={3}>
                        <Badge
                          colorPalette={STATUS_PALETTE[invoice.status] || "gray"}
                          variant="subtle"
                          fontFamily="space"
                          fontSize="10px"
                          letterSpacing="0.15em"
                          textTransform="uppercase"
                          px={2}
                          py={0.5}
                          borderRadius="sm"
                        >
                          {t.statuses[invoice.status] || invoice.status}
                        </Badge>
                        <Button
                          size="xs"
                          variant="outline"
                          borderColor="gray.300"
                          color="gray.700"
                          borderRadius="sm"
                          fontFamily="space"
                          fontSize="10px"
                          letterSpacing="0.1em"
                          textTransform="uppercase"
                          _hover={{ bg: "gray.100" }}
                          onClick={() => handleDownload(invoice.id)}
                        >
                          <Download size={12} />
                          {t.download}
                        </Button>
                      </Flex>
                    </Flex>
                  ))}
                </Box>
              ) : (
                <Text fontSize="sm" color="gray.500">{t.not}</Text>
              )}
            </Box>
          ))}
        </VStack>
      </Box>
    </Box>
  );
}

export default Invoices;
